import type { Middleware, UnknownAction } from '@reduxjs/toolkit';
import { isRejectedWithValue } from '@reduxjs/toolkit';
import type { api } from '@/redux/services/api';
import type { generatedApi as headerApi } from '@/redux/services/generatedApi/header';
import { showMessage } from '@/redux/slices/message';
import getTranslation from '@/util/getTranslation';
import type { RootState } from '@/redux/types';
import extractResponseMessage from '@/util/extractResponseMessage';
import endpointsWithTranslations from './endpointsWithTranslations';

type EndpointName = keyof typeof api.endpoints | keyof typeof headerApi.endpoints;

const unusedEndpoints: EndpointName[] = [
  'getSubscriptionPriceEstimate',
  'getApiHeader',
  'getFooterInfo',
  'getAccountData',
];

interface Action extends UnknownAction {
  meta?: {
    arg?: {
      endpointName: EndpointName;
    };
  };
}

const errorHandler: Middleware<unknown, RootState> =
  (middlewareApi) => (next) => (a) => {
    const { dispatch } = middlewareApi;
    const action = a as Action;
    if (!action.meta?.arg?.endpointName) {
      return next(action);
    }
    const { endpointName } = action.meta.arg;

    if (isRejectedWithValue(action)) {
      let messageShowedFromResponse = false;

      const responseMessage = extractResponseMessage(action.payload);

      if (responseMessage) {
        messageShowedFromResponse = true;
        dispatch(
          showMessage({
            message: responseMessage,
            variant: 'danger',
          }),
        );
      }

      if (
        !messageShowedFromResponse &&
        endpointName in endpointsWithTranslations &&
        !unusedEndpoints.includes(endpointName)
      ) {
        const { translations } = middlewareApi.getState();
        const translation = endpointsWithTranslations[endpointName];

        if (translation) {
          const message = getTranslation(translation, translations);

          dispatch(
            showMessage({
              message,
              variant: 'danger',
            }),
          );
        }
      }
    }
    return next(action);
  };

export default errorHandler;
